import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';

import { useSelector } from 'react-redux';
import { addMonths } from 'date-fns';
import { Container } from './styles';

import CotacaoService from '../../services/CotacaoService';
import AuthService from '../../services/AuthService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  calcNaxItemsPage,
  isScreenMobile,
  formatDate,
} from '../../util/functions';
import LabelSp from '../../components/LabelSp';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import CotacaoCrud from './crud';
import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';
import DropDownLazy from '../../components/DropdownLazy';
import EmpresaService from '../../services/EmpresaService';
import CalendarSp from '../../components/CalendarSp';

import { cfgPtBr } from '../../config/Constantes';

import InputMaskSp from '../../components/InputMaskSp';
import DropdownSp from '../../components/DropdownSp';

export default function Cotacao(props) {
  // useSelectors
  const filterVisible = useSelector(state => state.global.filterVisible);

  // useMemo
  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const usuarioLogado = AuthService.getUsuario();

  const filterService = useMemo(() => {
    const f = CotacaoService.getFilter();
    if (usuarioLogado.empresa) {
      f.idEmpresa = usuarioLogado.empresa.id;
    }
    return f;
  }, [usuarioLogado.empresa]);

  const empresaUsuario = usuarioLogado.empresa
    ? { value: usuarioLogado.empresa.id, label: usuarioLogado.empresa.razaoSocial }
    : null;

  // useStates
  const toBack = pageParams.toBack || '/cotacoes';

  const [filter, setFilter] = useState(filterService);
  const [cotacoes, setCotacoes] = useState([]);
  const [empresa, setEmpresa] = useState(empresaUsuario);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [funcTimeOut, setFuncTimeOut] = useState();

  const loadEmpresa = useCallback(async _razaoSocial => {
    if (_razaoSocial !== undefined) {
      const r = await EmpresaService.findAll({ razaoSocial: _razaoSocial, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.razaoSocial,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(8, 12, 18);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();
      try {
        const result = await CotacaoService.findAll(_filter);
        setCotacoes(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  const setFilterAndSearch = useCallback(
    async _filterValue => {
      if (JSON.stringify(_filterValue) !== JSON.stringify(filter)) {
        setFilter(_filterValue);

        if (funcTimeOut) {
          clearTimeout(funcTimeOut);
        }
        const func = setTimeout(async () => {
          handleBuscar(_filterValue);
        }, 800);
        setFuncTimeOut(func);
      }
    },
    [filter, funcTimeOut, handleBuscar]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  // functions
  function getTitle() {
    const titleDefault = 'Cotação';
    let titleAdd = '';

    if (pageParams.stateScreen === StateScreen.stSearch) {
      titleAdd = '';
    }
    if (pageParams.stateScreen === StateScreen.stInsert) {
      titleAdd = '(Novo)';
    }
    if (pageParams.stateScreen === StateScreen.stUpdate) {
      titleAdd = `(Alterando Id: ${pageParams.idSelected})`;
    }
    if (pageParams.stateScreen === StateScreen.stView) {
      titleAdd = ` (Visualizando Id: ${pageParams.idSelected})`;
    }

    if (!isScreenMobile()) {
      return `${titleDefault} ${titleAdd}`;
    }

    return titleDefault;
  }

  // useEffects
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    // desativado para evitar que a cada vez que o cotacao digitasse o sistema buscasse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffects
  // Responsavel por recalcular a quantidade de registros da grid quando a div do filtro ocultar
  // ou ficar visivel
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);

  // renders
  return (
    <Container className="container-page">
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          disabledButtonInsert={!AuthService.checkRoles('ATENDIMENTO_INSERT')}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonInsert={false}
          showButtonSearch
          titleFilterDefault="Buscar por Responsável"
          handleFilterDefault={text => {
            filter.nomeColaboradorGerencial = text;
            setFilter({ ...filter, nomeColaboradorGerencial: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              dataInicio: addMonths(new Date(), -1),
              dataFim: new Date(),
              idEmpresa: undefined,
              situacao: '',
              numeroContrato: '',
            });
            setEmpresa(null);
          }}
          handleButtonInsert={() => history.push('/cotacoes/insert')}
          handleButtonSearch={() => handleBuscar(filter)}
        >
          <div className="p-col-6 p-sm-2 p-lg-2 p-fluid">
            <LabelSp htmlFor="dataInicial">De: </LabelSp>
            <CalendarSp
              readOnlyInput
              appendTo={document.body}
              dateFormat="dd/mm/yy"
              yearNavigator
              locale={cfgPtBr}
              id="dataInicial"
              value={filter.dataInicio}
              yearRange="2010:2040"
              onChange={e => {
                setFilterAndSearch({ ...filter, dataInicio: e.value });
              }}
            />
          </div>
          <div className="p-col-6 p-sm-2 p-lg-2 p-fluid">
            <LabelSp htmlFor="dataFinal">Até: </LabelSp>
            <CalendarSp
              appendTo={document.body}
              readOnlyInput
              locale={cfgPtBr}
              id="dataFinal"
              dateFormat="dd/mm/yy"
              value={filter.dataFim}
              yearNavigator
              yearRange="2010:2040"
              onChange={e => setFilterAndSearch({ ...filter, dataFim: e.value })}
            />
          </div>

          <div className="p-col-6 p-sm-2 p-lg-2 p-fluid">
            <LabelSp>Nr. Contrato</LabelSp>
            <InputMaskSp
              mask="999999"
              value={filter.numeroContrato}
              onChange={e => {
                setFilterAndSearch({ ...filter, numeroContrato: e.target.value });
              }}
            />
          </div>

          <div className="p-col-6 p-sm-2 p-lg-2 p-fluid">
            <LabelSp>Situação</LabelSp>
            <DropdownSp
              value={filter.situacao}
              options={[
                { label: 'Todos', value: '' },
                { label: 'Aberta', value: '0' },
                { label: 'Encerrada', value: '1' },
                { label: 'Cancelada', value: '1' },
              ]}
              filterInputAutoFocus={false}
              onChange={e => {
                setFilterAndSearch({ ...filter, inativo: e.target.value });
              }}
            />
          </div>

          <div className="p-col-12 p-sm-4 p-lg-4 p-fluid">
            <LabelSp>Empresa</LabelSp>
            <DropDownLazy
              autoLoad
              showClear
              disabled={!!usuarioLogado.empresa}
              placeholder="Todas"
              onChange={e => {
                setFilterAndSearch({ ...filter, idEmpresa: e?.value });
                setEmpresa(e);
              }}
              value={empresa}
              onFilter={async txtFilter => {
                const retorno = await loadEmpresa(txtFilter);
                return retorno;
              }}
            />
          </div>
        </CrudHeader>
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>
    </Container>
  );

  function renderSearch() {
    return (
      <>
        <div className="p-col-12 p-fluid">
          <DataTableSp
            value={cotacoes}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              className="grid-col-data"
              header="Dt. Incio"
              body={rowData => formatDate(rowData.dataInicio, 'dd/MM/yyyy')}
            />
            <Column
              className="grid-col-hora"
              header="Hora"
              body={rowData => rowData.horaInicio}
            />
            <Column
              className="grid-col-data"
              header="Dt. Fim"
              body={rowData => formatDate(rowData.dataFim, 'dd/MM/yyyy')}
            />
            <Column
              className="grid-col-hora"
              header="Hora"
              body={rowData => rowData.horaFim}
            />
            <Column
              field="numeroContrato"
              className="grid-col grid-col-data p-p-6"
              header="Contrato"
            />
            <Column
              className="grid-col"
              style={{ width: 120 }}
              header="Situação"
              body={rowData => (rowData.situacao === 0 ? 'Em Processo' : 'Concluído')}
            />
            <Column
              style={{ width: 200 }}
              field="nomeColaboradorGerencial"
              className="grid-col p-p-6"
              header="Responsável"
            />

            <Column className="grid-col " field="nomeEmpresaGerencial" header="Empresa" />

            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonOp(rowData) {
    return (
      <BotaoMenuGrid
        handles={[
          () => history.push(`/cotacoes/${rowData.id}?view`),
          // () => history.push(`/cotacoes/${rowData.id}`),
          // () => confirmaExclusao(rowData.id),
        ]}
        disableds={[false, true, true]}
      />
    );
  }

  function renderCrud() {
    return (
      <CotacaoCrud
        idSelected={pageParams.idSelected}
        stateScreen={pageParams.stateScreen}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}
